import { useState, useMemo } from "react";
import { Table, Switch } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, EyeOutlined, SendOutlined } from "@ant-design/icons";
import { IconButton, PageHelperText, Badge, Pagination } from "@/components/ui";
import {
  AUTOMATED_EMAIL_AUDIENCE_LABELS,
  DEFAULT_AUTOMATED_EMAIL_AUDIENCE,
  getAutomatedEmailTitle,
  isAudienceSelectable,
  isManualSendTemplate,
  type AutomatedEmail,
} from "../types";

const PAGE_SIZE = 10;

type Props = {
  emails: AutomatedEmail[];
  eventSlug: string;
  onToggle: (id: string, enabled: boolean) => void;
  onPreview: (email: AutomatedEmail) => void;
  onEdit: (email: AutomatedEmail) => void;
  /** Releases a manual-send template. Omit to hide the Send action entirely. */
  onSend?: (email: AutomatedEmail) => void;
};

const AutomatedEmailList = ({ emails, onToggle, onPreview, onEdit, onSend }: Props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const paginatedEmails = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return emails.slice(startIndex, startIndex + pageSize);
  }, [emails, currentPage, pageSize]);

  const hasManualSendTemplate = useMemo(
    () => Boolean(onSend) && emails.some((email) => isManualSendTemplate(email.templateType)),
    [emails, onSend]
  );

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const columns: ColumnsType<AutomatedEmail> = [
    {
      title: "Template Name",
      dataIndex: "templateName",
      key: "templateName",
      sorter: (a, b) => getAutomatedEmailTitle(a).localeCompare(getAutomatedEmailTitle(b)),
      render: (_: string, record: AutomatedEmail) => (
        <div className="flex items-center gap-2">
          <span>{getAutomatedEmailTitle(record)}</span>
          {isAudienceSelectable(record.templateType) && (
            <Badge variant="default">
              {
                AUTOMATED_EMAIL_AUDIENCE_LABELS[
                  record.audience || DEFAULT_AUTOMATED_EMAIL_AUDIENCE
                ]
              }
            </Badge>
          )}
        </div>
      ),
    },
    {
      title: "Sender Name",
      dataIndex: "senderName",
      key: "senderName",
      sorter: (a, b) => a.senderName.localeCompare(b.senderName),
    },
    {
      title: "Sender Email",
      dataIndex: "senderEmail",
      key: "senderEmail",
      sorter: (a, b) => a.senderEmail.localeCompare(b.senderEmail),
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      align: "center",
      sorter: (a, b) => (a.enabled === b.enabled ? 0 : a.enabled ? -1 : 1),
      render: (enabled: boolean, record: AutomatedEmail) => (
        <Switch
          checked={enabled}
          onChange={(checked) => onToggle(record.id, checked)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 150,
      render: (_: unknown, record: AutomatedEmail) => (
        <div className="flex items-center justify-center gap-2">
          <IconButton
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
            ariaLabel="Edit template"
            title="Edit"
          />
          <IconButton
            icon={<EyeOutlined />}
            onClick={() => onPreview(record)}
            ariaLabel="Preview template"
            title="Preview"
          />
          {onSend && isManualSendTemplate(record.templateType) && (
            // Disabled rather than hidden while the template is off: the server
            // refuses a send from a disabled template, so offering the action
            // would only produce a bounce. The tooltip says which switch to flip.
            <IconButton
              icon={<SendOutlined />}
              onClick={() => onSend(record)}
              disabled={!record.enabled}
              ariaLabel="Send now"
              title={record.enabled ? "Send now" : "Enable this email before sending"}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-4">
        <div className="mb-2">
          <Badge variant="info">Note:</Badge>
        </div>
        <PageHelperText>
          Toggle on to enable automated email.
          {hasManualSendTemplate &&
            " Templates with a Send button never go out on their own — enable one, then send it when you are ready."}
        </PageHelperText>
      </div>

      <Table
        columns={columns}
        dataSource={paginatedEmails}
        rowKey="id"
        className="ava-data-table"
        showSorterTooltip={false}
        pagination={false}
      />

      {emails.length > pageSize && (
        <div className="mt-4">
          <Pagination
            current={currentPage}
            total={emails.length}
            pageSize={pageSize}
            onChange={handlePageChange}
            pageSizeOptions={["10", "20", "50"]}
          />
        </div>
      )}
    </div>
  );
};

export default AutomatedEmailList;
